/**
 * The Payments → ledger match (#3379): pure functions over rows read from the cell.
 *
 * Transform: Paymob's transactions export and Kashier's payout report become one Payment shape
 * (EGP numbers, signed: refunds and chargebacks are negative; the Cairo calendar day; the payout
 * batch). Fees and nets the reader may not see stay null and say so; nothing is guessed.
 *
 * Match, per transaction, in order:
 *  1. A chargeback always needs a person.
 *  2. A second successful capture of the same store order and amount is a duplicate capture.
 *  3. A fee that is not the provider's contract (percentage + fixed fee, 14% VAT on the fee) is a
 *     fee mismatch (only where the reader may see fees).
 *  4. A capture whose provider id is a submitted Payment Entry's reference_no is posted. Otherwise
 *     the store order's ERPNext Sales Order leads to its invoice: still owed → to post (the promoted
 *     sync proposes the Payment Entry); already paid → needs a person; none → no invoice.
 *  5. A refund is booked when the invoice has a credit note for the refunded amount.
 *
 * Match, per payout batch: its lines must sum to what the provider says it paid; the bank statement
 * line carrying the batch's bank reference is its deposit (else one with the same amount within two
 * days, for a person to confirm); a transferred batch with neither never reached the bank.
 */

export type Provider = "Paymob" | "Kashier"
export type Kind = "capture" | "refund" | "chargeback"

export type TxnReason = "posted" | "to_post" | "refund_booked" | "fee_mismatch" | "duplicate_capture" | "no_invoice" | "already_paid" | "refund_no_credit_note" | "chargeback"
export type BatchReason = "deposited" | "deposit_by_amount" | "batch_not_sum" | "missing_payout" | "deposit_mismatch"
export type Reason = TxnReason | BatchReason

/** One provider movement in the shared shape. `null` money is unreadable or hidden (`feeHidden`). */
export interface Payment {
  readonly key: string
  readonly provider: Provider
  readonly id: string
  readonly kind: Kind
  /** The store order the checkout paid (Shopify's numeric order id). */
  readonly orderRef: string
  /** The provider's timestamp text, exactly as exported. */
  readonly at: string
  /** The Cairo calendar day of the movement. */
  readonly day: string | null
  readonly amountText: string
  /** EGP, signed: refunds and chargebacks are negative. */
  readonly gross: number | null
  readonly fee: number | null
  readonly vat: number | null
  readonly net: number | null
  readonly feeHidden: boolean
  readonly method: string
  readonly instrument: string | null
  readonly instrumentHidden: boolean
  readonly batch: string | null
  readonly parent: string | null
  readonly version: number
}

export interface Batch {
  readonly key: string
  readonly provider: Provider
  readonly id: string
  readonly date: string | null
  /** What the provider says it paid out, EGP; null when hidden or unreadable. */
  readonly reportedNet: number | null
  readonly netHidden: boolean
  readonly bankReference: string | null
  readonly transferred: boolean
  readonly version: number
}

export interface BankLine {
  readonly key: string
  readonly reference: string
  readonly date: string | null
  readonly description: string
  readonly credit: number | null
  readonly debit: number | null
  /** Bank amounts are Finance's: a payout's deposit would reveal its net. */
  readonly amountHidden: boolean
  readonly version: number
}

export interface StoreOrder {
  /** Shopify's numeric order id, the provider's merchant order id. */
  readonly orderId: string
  readonly name: string
  /** The ERPNext Sales Order the store's sync recorded, if any. */
  readonly reference: string | null
}

export interface LedgerInvoice {
  readonly name: string
  readonly customer: string
  readonly date: string | null
  readonly total: number | null
  readonly outstanding: number | null
  readonly isReturn: boolean
  readonly returnAgainst: string | null
  readonly salesOrder: string | null
  readonly submitted: boolean
  readonly version: number
}

export interface LedgerPayment {
  readonly name: string
  readonly referenceNo: string | null
  readonly mode: string | null
  readonly amount: number | null
  readonly invoice: string | null
  readonly date: string | null
  readonly submitted: boolean
  readonly version: number
}

export interface LedgerJournal {
  readonly name: string
  readonly chequeNo: string | null
  readonly voucherType: string
  readonly date: string | null
  readonly total: number | null
  readonly submitted: boolean
  readonly version: number
}

/** Merchant contracts: a percentage and a fixed EGP fee per capture, VAT on the fee. */
export const CONTRACTS: Record<Provider, { readonly rate: number; readonly fixed: number; readonly vat: number }> = {
  Paymob: { rate: 0.0275, fixed: 3, vat: 0.14 },
  Kashier: { rate: 0.0285, fixed: 3, vat: 0.14 },
}

const round2 = (value: number) => Math.round(value * 100) / 100
const TOLERANCE = 0.005

/** Decimal text to a number; null for anything that is not plain decimal text. */
export function decimal(text: string | null | undefined): number | null {
  if (typeof text !== "string" || !/^-?\d+(\.\d+)?$/.test(text.trim())) return null
  const value = Number(text.trim())
  return Number.isFinite(value) ? value : null
}

/** Piastre text (Paymob's amount_cents) to EGP. */
export function piastres(text: string | null | undefined): number | null {
  const value = decimal(text)
  return value === null || !Number.isInteger(value) ? null : value / 100
}

function realDay(date: string | undefined): string | null {
  if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return null
  const parsed = Date.parse(`${date}T00:00:00Z`)
  return Number.isFinite(parsed) && new Date(parsed).toISOString().slice(0, 10) === date ? date : null
}

/** Paymob's created_at is Cairo wall-clock time without an offset: its date part is the day. */
export function cairoDay(local: string | null | undefined): string | null {
  return realDay(typeof local === "string" ? /^(\d{4}-\d{2}-\d{2})T/.exec(local)?.[1] : undefined)
}

/** Kashier's transactionDate is a UTC instant: the Cairo day is three hours later (summer time). */
export function cairoDayOfInstant(instant: string | null | undefined): string | null {
  if (typeof instant !== "string" || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?Z$/.test(instant)) return null
  const at = Date.parse(instant)
  return Number.isFinite(at) ? new Date(at + 3 * 3_600_000).toISOString().slice(0, 10) : null
}

export function dateOnly(text: string | null | undefined): string | null {
  return realDay(text ?? undefined)
}

/** Monday of the ISO week a day falls in. */
export function weekOf(date: string): string {
  const d = new Date(`${date}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7))
  return d.toISOString().slice(0, 10)
}

export function dayDiff(a: string, b: string): number {
  return Math.round((Date.parse(`${a}T00:00:00Z`) - Date.parse(`${b}T00:00:00Z`)) / 86_400_000)
}

/** What the contract charges on a capture of this amount, and the VAT on it. */
export function contractFee(provider: Provider, gross: number) {
  const c = CONTRACTS[provider]
  const fee = round2(gross * c.rate + c.fixed)
  return { fee, vat: round2(fee * c.vat) }
}

export interface TxnFinding {
  readonly id: string
  readonly subject: "transaction"
  readonly payment: Payment
  readonly reason: TxnReason
  readonly confidence: number
  /** The ledger document the finding rests on: a Payment Entry, invoice or credit note. */
  readonly erp: string | null
  readonly invoice: LedgerInvoice | null
  readonly store: StoreOrder | null
  /** The contract's fee and VAT, when the reader may see fees. */
  readonly expected: { readonly fee: number; readonly vat: number } | null
  /** Gross minus what the ledger holds (Payment Entry amount or invoice still owed), when both are known. */
  readonly delta: number | null
  /** The first capture this one repeats. */
  readonly duplicateOf: string | null
  /** The ledger documents the verdict rests on, at their versions (`name@version`), for the decision key. */
  readonly evidence: string
}

export interface BatchFinding {
  readonly id: string
  readonly subject: "batch"
  readonly batch: Batch
  readonly reason: BatchReason
  readonly confidence: number
  readonly erp: string | null
  readonly lines: number
  /** The sum of the batch's line nets, when every one is readable. */
  readonly lineNet: number | null
  readonly gross: number | null
  readonly fees: number | null
  readonly deposit: BankLine | null
  /** The fee Journal Entry booked for this batch, if any. */
  readonly feeJournal: LedgerJournal | null
  readonly delta: number | null
  /** The batch's lines at their versions, for the decision key: a corrected line reopens the batch. */
  readonly evidence: string
}

export type Finding = TxnFinding | BatchFinding

export interface Ledger {
  readonly stores: readonly StoreOrder[]
  readonly invoices: readonly LedgerInvoice[]
  readonly payments: readonly LedgerPayment[]
  readonly journals: readonly LedgerJournal[]
}

const agree = (a: number | null, b: number | null) => a !== null && b !== null && Math.abs(a - b) <= TOLERANCE

export function reconcileTransactions(movements: readonly Payment[], ledger: Ledger): TxnFinding[] {
  const stores = new Map(ledger.stores.map((s) => [s.orderId, s]))
  const invoiceBySalesOrder = new Map<string, LedgerInvoice>()
  const creditNotes = new Map<string, LedgerInvoice[]>()
  for (const inv of ledger.invoices) {
    if (!inv.submitted) continue
    if (inv.isReturn && inv.returnAgainst) creditNotes.set(inv.returnAgainst, [...(creditNotes.get(inv.returnAgainst) ?? []), inv])
    else if (!inv.isReturn && inv.salesOrder && !invoiceBySalesOrder.has(inv.salesOrder)) invoiceBySalesOrder.set(inv.salesOrder, inv)
  }
  const paymentByReference = new Map(ledger.payments.filter((p) => p.submitted && p.referenceNo).map((p) => [`${p.referenceNo}`, p]))
  const firstCapture = new Map<string, Payment>()
  const claimedNotes = new Set<string>()
  const ordered = [...movements].sort((a, b) => a.at.localeCompare(b.at) || a.id.localeCompare(b.id))
  const findings: TxnFinding[] = []
  for (const p of ordered) {
    const store = stores.get(p.orderRef) ?? null
    const invoice = store?.reference ? (invoiceBySalesOrder.get(store.reference) ?? null) : null
    const expected = p.kind === "capture" && p.gross !== null && !p.feeHidden ? contractFee(p.provider, p.gross) : null
    const base = { id: `transaction:${p.key}`, subject: "transaction" as const, payment: p, store, invoice, expected, duplicateOf: null, evidence: invoice ? `${invoice.name}@${invoice.version}` : "" }
    if (p.kind === "chargeback") {
      findings.push({ ...base, reason: "chargeback", confidence: 90, erp: invoice?.name ?? null, delta: null })
      continue
    }
    if (p.kind === "refund") {
      // Each credit note covers one refund: refunds take the unclaimed note of their amount first,
      // else any unclaimed note (a person checks the gap). A refund left with none is unbooked.
      const notes = (invoice ? (creditNotes.get(invoice.name) ?? []) : []).filter((n) => !claimedNotes.has(n.name))
      const amount = p.gross === null ? null : Math.abs(p.gross)
      const note = notes.find((n) => agree(n.total === null ? null : Math.abs(n.total), amount)) ?? notes[0]
      if (note) {
        claimedNotes.add(note.name)
        const exact = agree(Math.abs(note.total ?? 0), amount)
        findings.push({ ...base, reason: "refund_booked", confidence: exact ? 100 : 70, erp: note.name, delta: note.total !== null && amount !== null ? round2(amount - Math.abs(note.total)) : null, evidence: `${base.evidence}|${note.name}@${note.version}` })
      } else findings.push({ ...base, reason: "refund_no_credit_note", confidence: invoice ? 90 : 60, erp: invoice?.name ?? null, delta: null })
      continue
    }
    const repeatKey = `${p.provider}\0${p.orderRef}\0${p.gross}`
    const first = firstCapture.get(repeatKey)
    const posted = paymentByReference.get(p.id) ?? null
    if (first) {
      const firstPosted = paymentByReference.get(first.id)
      findings.push({ ...base, reason: "duplicate_capture", confidence: 95, erp: firstPosted?.name ?? invoice?.name ?? null, delta: null, duplicateOf: first.id, evidence: `${base.evidence}|${first.key}@${first.version}|${firstPosted ? `${firstPosted.name}@${firstPosted.version}` : ""}` })
      continue
    }
    firstCapture.set(repeatKey, p)
    if (expected && (!agree(p.fee, expected.fee) || !agree(p.vat, expected.vat))) {
      findings.push({ ...base, reason: "fee_mismatch", confidence: 90, erp: posted?.name ?? invoice?.name ?? null, evidence: posted ? `${base.evidence}|${posted.name}@${posted.version}` : base.evidence, delta: p.fee !== null && p.vat !== null ? round2(p.fee + p.vat - expected.fee - expected.vat) : null })
      continue
    }
    if (posted) {
      // The Payment Entry must be this provider's, for this amount, against this order's invoice;
      // any disagreement keeps the verdict but sends it to a person.
      const delta = p.gross !== null && posted.amount !== null ? round2(p.gross - posted.amount) : null
      const consistent = delta !== null && Math.abs(delta) <= TOLERANCE && posted.mode === p.provider && invoice !== null && posted.invoice === invoice.name
      findings.push({ ...base, reason: "posted", confidence: consistent ? 100 : 60, erp: posted.name, delta, evidence: `${base.evidence}|${posted.name}@${posted.version}` })
      continue
    }
    if (!invoice) {
      findings.push({ ...base, reason: "no_invoice", confidence: store ? 85 : 75, erp: null, delta: null })
      continue
    }
    if (invoice.outstanding !== null && invoice.outstanding <= TOLERANCE) {
      findings.push({ ...base, reason: "already_paid", confidence: 80, erp: invoice.name, delta: null })
      continue
    }
    const delta = p.gross !== null && invoice.outstanding !== null ? round2(p.gross - invoice.outstanding) : null
    // ERPNext rounds what an invoice still owes to the whole pound (its rounded total); a capture of
    // the exact grand total is that invoice, with less than a pound of rounding between them.
    const rounding = delta !== null && Math.abs(delta) < 1 && agree(p.gross, invoice.total)
    findings.push({ ...base, reason: "to_post", confidence: delta === null || Math.abs(delta) <= TOLERANCE || rounding ? 95 : 70, erp: invoice.name, delta })
  }
  return findings
}

/** Payout batches: Paymob's from its settlement report, Kashier's grouped from its payout lines. */
export function reconcileBatches(batches: readonly Batch[], movements: readonly Payment[], bank: readonly BankLine[], journals: readonly LedgerJournal[], today: string): BatchFinding[] {
  const lines = new Map<string, Payment[]>()
  for (const p of movements) if (p.batch) lines.set(`${p.provider}\0${p.batch}`, [...(lines.get(`${p.provider}\0${p.batch}`) ?? []), p])
  const byReference = new Map(bank.map((b) => [b.reference, b]))
  const feeJournals = new Map(journals.filter((j) => j.submitted && j.chequeNo && j.voucherType !== "Bank Entry").flatMap((j) => (j.chequeNo ? [[j.chequeNo, j] as const] : [])))
  const bankJournals = new Map(journals.filter((j) => j.submitted && j.chequeNo && j.voucherType === "Bank Entry").flatMap((j) => (j.chequeNo ? [[j.chequeNo, j] as const] : [])))
  const claimed = new Set<string>()
  const findings: BatchFinding[] = []
  // Exact references first, so an amount-only search never takes a line a reference names.
  for (const b of batches) if (b.bankReference && byReference.has(b.bankReference)) claimed.add(b.bankReference)
  const sorted = [...batches].sort((a, b) => (a.date ?? "").localeCompare(b.date ?? "") || a.id.localeCompare(b.id))
  for (const b of sorted) {
    const own = lines.get(`${b.provider}\0${b.id}`) ?? []
    const readable = own.every((p) => p.net !== null)
    const lineNet = readable ? round2(own.reduce((s, p) => s + (p.net ?? 0), 0)) : null
    const gross = own.every((p) => p.gross !== null) ? round2(own.filter((p) => p.kind === "capture").reduce((s, p) => s + (p.gross ?? 0), 0)) : null
    const fees = own.every((p) => p.fee !== null && p.vat !== null) ? round2(own.reduce((s, p) => s + (p.fee ?? 0) + (p.vat ?? 0), 0)) : null
    const exact = b.bankReference ? (byReference.get(b.bankReference) ?? null) : null
    const date = b.date
    const byAmount =
      exact || b.reportedNet === null || !date ? null : (bank.find((l) => !claimed.has(l.reference) && l.date && Math.abs(dayDiff(l.date, date)) <= 2 && agree(l.credit, b.reportedNet)) ?? null)
    if (byAmount) claimed.add(byAmount.reference)
    const deposit = exact ?? byAmount
    const base = {
      id: `batch:${b.key}`, subject: "batch" as const, batch: b, lines: own.length, lineNet, gross, fees, deposit,
      evidence: `${own.map((p) => `${p.id}@${p.version}`).sort().join(",")}|${deposit ? `${deposit.reference}@${deposit.version}` : ""}`,
      feeJournal: feeJournals.get(b.id) ?? null, erp: (b.bankReference ? bankJournals.get(b.bankReference)?.name : undefined) ?? feeJournals.get(b.id)?.name ?? null,
    }
    if (lineNet !== null && b.reportedNet !== null && !agree(lineNet, b.reportedNet)) findings.push({ ...base, reason: "batch_not_sum", confidence: 95, delta: round2(b.reportedNet - lineNet) })
    else if (!deposit) {
      // A batch the provider has not transferred yet is not missing; one it says it sent is.
      if (b.transferred && b.date && b.date < today) findings.push({ ...base, reason: "missing_payout", confidence: 90, delta: null })
    } else if (exact && b.reportedNet !== null && exact.credit !== null && !agree(exact.credit, b.reportedNet)) findings.push({ ...base, reason: "deposit_mismatch", confidence: 90, delta: round2(exact.credit - b.reportedNet) })
    else findings.push({ ...base, reason: exact ? "deposited" : "deposit_by_amount", confidence: exact ? 100 : 65, delta: null })
  }
  return findings
}

const CLEAN: ReadonlySet<Reason> = new Set(["posted", "refund_booked", "deposited", "to_post"])

/** A finding a person should look at: anything the rules could not settle with full confidence. */
export function needsReview(f: Finding): boolean {
  return !CLEAN.has(f.reason) || f.confidence < 90
}

/** FNV-1a over text, 8 hex digits: a short fingerprint of a finding's evidence. */
function fingerprint(text: string): string {
  let h = 0x811c9dc5
  for (let i = 0; i < text.length; i++) h = Math.imul(h ^ text.charCodeAt(i), 0x01000193) >>> 0
  return h.toString(16).padStart(8, "0")
}

/**
 * Every finding's decision record key: the subject at the versions the reviewer saw, and a
 * fingerprint of the ledger documents (or batch lines) the verdict rests on. When any of them
 * changes, the finding returns to the queue.
 */
export function decisionKey(f: Finding): string {
  return f.subject === "transaction"
    ? `${f.id}~v${f.payment.version}.${f.invoice?.version ?? 0}~${fingerprint(f.evidence)}`
    : `${f.id}~v${f.batch.version}.${f.deposit?.version ?? 0}~${fingerprint(f.evidence)}`
}

export interface Summary {
  readonly transactions: number
  readonly batches: number
  readonly posted: number
  readonly toPost: number
  readonly toPostAmount: number
  readonly review: number
  readonly byReason: Readonly<Record<Reason, number>>
  /** Gross EGP not yet reconciled to the ledger: to post plus every open finding. */
  readonly unreconciled: number
  /** Fees and VAT paid, when every fee is visible to the reader. */
  readonly fees: number | null
}

export const REASONS: readonly Reason[] = [
  "posted", "to_post", "refund_booked", "fee_mismatch", "duplicate_capture", "no_invoice", "already_paid", "refund_no_credit_note", "chargeback",
  "deposited", "deposit_by_amount", "batch_not_sum", "missing_payout", "deposit_mismatch",
]

export function summarize(txns: readonly TxnFinding[], batches: readonly BatchFinding[]): Summary {
  const byReason = Object.fromEntries(REASONS.map((r) => [r, 0])) as Record<Reason, number>
  for (const f of [...txns, ...batches]) byReason[f.reason]++
  const toPost = txns.filter((f) => f.reason === "to_post")
  const open = txns.filter((f) => f.reason !== "posted" && f.reason !== "refund_booked")
  const fees = txns.every((f) => f.payment.fee !== null && f.payment.vat !== null) ? round2(txns.reduce((s, f) => s + (f.payment.fee ?? 0) + (f.payment.vat ?? 0), 0)) : null
  return {
    transactions: txns.length,
    batches: batches.length,
    posted: byReason.posted + byReason.refund_booked,
    toPost: toPost.length,
    toPostAmount: round2(toPost.reduce((s, f) => s + (f.payment.gross ?? 0), 0)),
    review: [...txns, ...batches].filter(needsReview).length,
    byReason,
    unreconciled: round2(open.reduce((s, f) => s + Math.abs(f.payment.gross ?? 0), 0)),
    fees: txns.length ? fees : null,
  }
}

/** Fees and VAT by provider per ISO week of the movement; null when any fee is hidden. */
export function weeklyFees(txns: readonly TxnFinding[]): { week: string; Paymob: number; Kashier: number }[] | null {
  if (txns.some((f) => f.payment.feeHidden)) return null
  const weeks = new Map<string, { week: string; Paymob: number; Kashier: number }>()
  for (const { payment: p } of txns) {
    if (!p.day || p.fee === null || p.vat === null) continue
    const week = weekOf(p.day)
    const row = weeks.get(week) ?? { week, Paymob: 0, Kashier: 0 }
    row[p.provider] = round2(row[p.provider] + p.fee + p.vat)
    weeks.set(week, row)
  }
  return [...weeks.values()].sort((a, b) => a.week.localeCompare(b.week))
}

/** Gross EGP per ISO week that is not reconciled: waiting to be posted, or waiting for a person. */
export function weeklyUnreconciled(txns: readonly TxnFinding[]): { week: string; to_post: number; review: number; cumulative: number }[] {
  const weeks = new Map<string, { week: string; to_post: number; review: number; cumulative: number }>()
  for (const f of txns) {
    const day = f.payment.day
    if (!day) continue
    const week = weekOf(day)
    const row = weeks.get(week) ?? { week, to_post: 0, review: 0, cumulative: 0 }
    const amount = Math.abs(f.payment.gross ?? 0)
    if (f.reason === "to_post") row.to_post = round2(row.to_post + amount)
    else if (needsReview(f)) row.review = round2(row.review + amount)
    weeks.set(week, row)
  }
  let running = 0
  return [...weeks.values()]
    .sort((a, b) => a.week.localeCompare(b.week))
    .map((row) => {
      running = round2(running + row.to_post + row.review)
      return { ...row, cumulative: running }
    })
}
